import { Box, Flex, Text } from "@chakra-ui/react";
import { forwardRef } from "react";

/**
 * ColorSwatchField — a labelled colour input rendered as a compact pill with
 * a live swatch on the left, the hex value in Geist Mono, and a hidden but
 * fully-functional native colour input layered on top so the user can click
 * the swatch to open the OS colour picker.
 *
 * Use for theme override grids (light/dark colours, chart palette, brand
 * accent) where a row of small inputs would look noisy.
 *
 * Theme-aware via `--bg-secondary`, `--border-default`, `--text-*` tokens.
 */
export interface ColorSwatchFieldProps {
  label: string;
  value: string;
  onChange?: (next: string) => void;
  /** When true, renders as read-only (no native input overlay). */
  readOnly?: boolean;
}

export const ColorSwatchField = forwardRef<
  HTMLDivElement,
  ColorSwatchFieldProps
>(function ColorSwatchField({ label, value, onChange, readOnly }, ref) {
  return (
    <Flex ref={ref} direction="column" gap={1.5}>
      <Text fontSize="12px" color="var(--text-secondary-color)" m={0}>
        {label}
      </Text>
      <Flex
        align="center"
        gap={2}
        px={2.5}
        py={1.5}
        bg="var(--bg-secondary)"
        borderWidth="1px"
        borderColor="var(--border-default)"
        borderRadius="10px"
        position="relative"
      >
        <Box
          width="22px"
          height="22px"
          borderRadius="6px"
          bg={value}
          borderWidth="1px"
          borderColor="var(--border-default)"
          flexShrink={0}
        />
        <Text
          fontFamily="var(--font-mono)"
          fontSize="12px"
          color="var(--text-primary)"
          m={0}
          flex={1}
        >
          {value}
        </Text>
        {!readOnly && onChange && (
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            aria-label={label}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              opacity: 0,
              cursor: "pointer",
              border: "none",
              padding: 0,
              background: "transparent",
            }}
          />
        )}
      </Flex>
    </Flex>
  );
});

export default ColorSwatchField;
